/**
 * Client-side mirror of the server's declarative rate-configuration schema (v3).
 * Server source of truth: src/model/RateConfig.ts on the API project.
 *
 * IMPORTANT: every field in this file is JSON-safe (no functions, no string-eval).
 * The server-side rating engine evaluates the same data shape; this client copy
 * exists to (a) edit the JSON in a typed form and (b) compute live previews via
 * {@link RateConfigEvaluator} that match the server's results.
 *
 * v3 (2026-05-06) introduced the unified {@link PropertyCharacteristicGroup}
 * model. v2 documents are NOT supported — there were no production v2
 * documents at the time of the cutover. See `docs/contracts/shared/RateConfig.md`
 * for the canonical contract.
 */

/** Sentinel "open top" for QuantityBreak.maxQty. Mirrors server. */
export const MAX_QTY_SENTINEL = Number.MAX_SAFE_INTEGER;

/** Top-level keys of a {@link RateConfigDocument} are scopes. */
export const COMMON_SCOPES = ['PRINT_TO_ORDER', 'SHORT_RUN', 'PRINT_TO_PALLET'];

/**
 * Top-level rate-configuration document. Keys are SCOPES (e.g. 'SHORT_RUN'),
 * each pointing at a (possibly partial) {@link RateConfig}.
 *
 * The optional `_previewPageCounts` key holds the enumerated page-count
 * values used by the preview matrix. The optional
 * `_previewBookQuantities` key holds the enumerated book-quantity
 * values used by the preview matrix when the scope's factors operate
 * at the order level (PER_PACKAGE, `packageType`,
 * `carrierServiceLevel`, or `facility`) — each entry becomes a price
 * column on the matrix the same way each page count becomes a row.
 * The engine ignores both fields; the editor uses them to drive the
 * deterministic preview table.
 *
 * The optional `facilities` field lists the PrintNode / Facility IDs to
 * which this rate document applies. The client rate-config editor
 * populates it from the live active-facility catalog on every save,
 * and the server's `Orders.rate()` reads it from
 * `ServiceProvider.rateConfig` to build the per-facility rate table.
 * Absent or empty → no facilities → empty rate table. See
 * `docs/contracts/shared/RateConfig.md` and
 * `docs/contracts/inbox/_processed/2026-07-08-rateconfig-facilities-field.md`.
 *
 * NOTE ON THE INDEX SIGNATURE. Any TS-side traversal that iterates
 * `Object.keys(doc)` intending to visit only SCOPES must filter out
 * the reserved base keys (`_previewPageCounts`, `_previewBookQuantities`,
 * `facilities`); otherwise the reserved arrays get treated as scopes
 * and generate spurious tabs / validation errors. Use
 * {@link RESERVED_DOCUMENT_KEYS} / {@link isReservedDocumentKey} rather
 * than open-coding the list at every call site so a future addition
 * only needs updating in one place.
 */
export interface RateConfigDocument {
  [scope: string]: Partial<RateConfig> | number[] | undefined;
  _previewPageCounts?: number[];
  _previewBookQuantities?: number[];
  facilities?: number[];
}

/**
 * Reserved top-level keys of {@link RateConfigDocument} — anything the
 * editor / validator / evaluator must NOT treat as a scope. Kept as a
 * single source of truth so adding a new base-level field (e.g. a
 * future `_previewCurrencyOverrides`) only requires appending to this
 * list. Mirrors the server's `RateConfigDocument` reserved-key set.
 */
export const RESERVED_DOCUMENT_KEYS: ReadonlyArray<string> = Object.freeze([
  '_previewPageCounts',
  '_previewBookQuantities',
  'facilities'
]);

/** True iff `key` is a reserved base-level field, not a scope. */
export function isReservedDocumentKey(key: string): boolean {
  return RESERVED_DOCUMENT_KEYS.indexOf(key) >= 0;
}

/** Per-scope rate configuration. */
export interface RateConfig {
  /** Schema version. v3 is the only currently-supported value. */
  version: 3;
  /** ISO currency code; the engine treats values as in this currency. */
  currency: 'USD' | string;
  /** Ordered list of factors that contribute to the per-item / per-order cost. */
  factors: RateFactor[];
  /** Global cost floors applied AFTER all factors. */
  minimums?: { perItem?: number; perOrder?: number };
  /**
   * Scope-level quantity-break tier list. Drives:
   *  - the values offered when a propertyCharacteristic group includes
   *    `'quantityBreak'` (each tier becomes a cell-key token like `"1-4"`,
   *    or `"<min>-"` for the open-top tier);
   *  - the preview matrix tier columns.
   * When absent, the editor falls back to {@link DEFAULT_QUANTITY_BREAK_TIERS}.
   */
  defaultQuantityBreaks?: { minQty: number; maxQty: number }[];
  /**
   * Applied LAST. Multiplies final unitCost AND perOrderCost by
   * `(1 - percentageDiscount/100)`. The most common Organization-level override.
   */
  percentageDiscount?: number;
}

/** How a {@link RateFactor} contributes to the final cost. */
export type FactorKind =
  | 'PER_ORDER'              // contributes once to perOrderCost
  | 'PER_ITEM'               // contributes once to unitCost (independent of qty)
  | 'PER_QUANTITY'           // contributes amount per book (unitCost += amount * qty)
  | 'PER_NUMERIC_PROPERTY'   // contributes amount * round(item[property]) per book
  | 'PER_PACKAGE';           // contributes amount once per element of order.packageTypes[]

/** Allowed values for {@link FactorKind}, used by dropdowns. */
export const FACTOR_KINDS: FactorKind[] = ['PER_ORDER', 'PER_ITEM', 'PER_QUANTITY', 'PER_NUMERIC_PROPERTY', 'PER_PACKAGE'];

/**
 * Canonical UI-presentation order. Editor groups factors under
 * collapsible sections in this order; storage order is irrelevant.
 */
export const FACTOR_KIND_ORDER: FactorKind[] = ['PER_ORDER', 'PER_ITEM', 'PER_QUANTITY', 'PER_NUMERIC_PROPERTY', 'PER_PACKAGE'];

/** Item enum-valued properties carried on order items. */
export type ItemEnumProperty = 'interiorType' | 'bindingType' | 'laminateType' | 'paperType' | 'packageType' | 'carrierServiceLevel' | 'facility';

/** Item numeric-valued properties usable in {@link RateFactor.property}. */
export type ItemNumericProperty = 'pages' | 'width' | 'height' | 'weight' | 'thickness' | 'caseQuantity';

/**
 * The dimensions a factor's group can be conditioned on. Each is sourced
 * from a known place at evaluation time:
 *
 *  - bindingType / interiorType / laminateType — static enums on the item.
 *  - paperType — stringified `PaperType.ID` carried on the item.
 *  - packageType — stringified `PackageType.ID`. For book-level
 *    factor kinds (`PER_ITEM`, `PER_QUANTITY`, `PER_NUMERIC_PROPERTY`)
 *    this is taken from the item itself and is generally empty for
 *    production input (the validator forbids book-level kinds from
 *    grouping on `packageType`). For `PER_PACKAGE` factors it is
 *    taken from each iteration's element of `order.packageTypes[]`.
 *  - carrierServiceLevel — ORDER-level value (e.g. `'UPS_GROUND'`).
 *    Legal ONLY in `PER_ORDER` and `PER_PACKAGE` factors; the
 *    validator rejects it on book-level kinds because each book in
 *    the order shares the same order-level carrier choice, so
 *    grouping a per-book factor on it would be meaningless.
 *  - facility — ORDER-level stringified `Facility.ID` (a.k.a.
 *    PrintNode id). Every item in an order is produced/packed at a
 *    single facility, so this is a constant across all per-book
 *    iterations within an order. Legal on every {@link FactorKind}
 *    (a `PER_QUANTITY` rate that differs by facility is just as
 *    meaningful as a `PER_PACKAGE` one). Dynamic-value, sourced from
 *    the live `Facility` catalog the same way `paperType` is sourced
 *    from `PaperType`. The rating engine reads it from
 *    `OrderRateInput.facility`; the validator treats unknown ids as
 *    string tokens (no live precheck) to match the `paperType` /
 *    `packageType` / `carrierServiceLevel` conventions.
 *  - quantityBreak — derived from order quantity using the scope's
 *    `defaultQuantityBreaks`. Each tier is keyed `"<min>-<max>"`, with
 *    the open-top tier keyed `"<min>-"`. For `PER_PACKAGE` factors
 *    the quantity used is the order's book quantity (NOT the package
 *    count), which matches the "are we in the wholesale tier?"
 *    semantic the tiers describe.
 *  - numberUp — derived from the item's body width/height by the shared
 *    Util in `numberUp.ts`. Active values: `'2UP'`, `'4UP'`. Extensible.
 *    Book-level only; the validator rejects it on `PER_PACKAGE`
 *    factors (a package's width/height are different units of meaning).
 */
export type PropertyCharacteristic =
  | 'bindingType'
  | 'interiorType'
  | 'laminateType'
  | 'paperType'
  | 'packageType'
  | 'carrierServiceLevel'
  | 'facility'
  | 'quantityBreak'
  | 'numberUp';

/** All propertyCharacteristics in a stable order, for editor enumeration. */
export const PROPERTY_CHARACTERISTICS: PropertyCharacteristic[] =
  ['bindingType', 'interiorType', 'laminateType', 'paperType', 'packageType', 'carrierServiceLevel', 'facility', 'quantityBreak', 'numberUp'];

/** Wildcard token used in cell keys to mean "this position is non-differentiating". */
export const CELL_WILDCARD = '*';

/**
 * "Collapsed" sentinel — semantically identical to {@link CELL_WILDCARD}
 * for matching purposes, but signals to the editor that the user has
 * chosen NOT to differentiate by this characteristic for this slice of
 * the matrix. The editor renders the corresponding axis position as a
 * single non-expanding row/col instead of one row/col per enumerated
 * value.
 *
 * Resolution rule: `-` matches any value at its position, exactly like
 * `*`. The two are NEVER both authored for the same canonical combo
 * (the validator forbids it); if they somehow do coexist at runtime,
 * `*` wins deterministically as a defensive fallback.
 *
 * Hash rule: in {@link RateFactorId.computeFactorId}, `-` tokens are
 * normalized to `*` before hashing so the auto-derived factor `id`
 * stays stable as the user toggles editor expand/collapse on an axis.
 */
export const CELL_COLLAPSED = '-';

/** Cell-key token separator. Tokens never contain this character. */
export const CELL_KEY_SEP = '|';

/** True if `tok` is a wildcard sentinel (matches anything at its position). */
export function isWildcardToken(tok: string): boolean {
  return tok === CELL_WILDCARD || tok === CELL_COLLAPSED;
}

/**
 * A single cell value.
 *
 *  - `amount` set, no `infeasible` → contributes `amount` to the
 *    factor's matched sum.
 *  - `infeasible: true` (no `amount` needed) → causes a loud failure
 *    on match (engine throws `RateInfeasibleError`).
 *  - **Empty `{}`** (neither `amount` nor `infeasible`) → contributes
 *    nothing AT THIS LAYER and the engine continues resolution to the
 *    next less-specific cell. Useful as an authoring convenience to
 *    "punch a hole" through which a wider-wildcard cell shows through.
 *    The validator requires that empty cells have a fallback that DOES
 *    supply a value; otherwise it reports a coverage error.
 */
export interface RateCell {
  amount?: number;
  infeasible?: boolean;
}

/**
 * One propertyCharacteristic group on a factor.
 *
 * Cell keys are `|`-joined value tuples in `characteristics` order.
 * Each token may be a literal characteristic value, `'*'` (wildcard),
 * or `'-'` (collapsed — same matching semantics as `'*'`, different
 * editor presentation). The engine resolves cells exact-first per
 * position, then falls back to `'*'`/`'-'` per position left-to-right
 * in fewest-wildcards-first order.
 */
export interface PropertyCharacteristicGroup {
  /** Ordered list of characteristics defining the group's combo-key positions. */
  characteristics: PropertyCharacteristic[];
  /** Sparse map keyed by `|`-joined value tuple. */
  cells: { [comboKey: string]: RateCell };

  /**
   * EDITOR-ONLY companion fields recording the user's pivot-table
   * layout choice for this group's matrix. Both arrays together MUST
   * be a permutation of `characteristics` (every characteristic
   * appears in exactly one of the two axes). The first entry of each
   * array is the OUTERMOST nesting level on its axis; subsequent
   * entries nest inside.
   *
   * The rating engine and the validator MUST IGNORE these fields —
   * they have no semantic meaning and are NOT included in
   * `computeFactorId(...)`. They are preserved on round-trip by the
   * server's entity GET/PUT path so the user's layout persists across
   * sessions and across users.
   *
   * When absent (or when the union doesn't cover `characteristics` —
   * e.g. the user added a characteristic in a session that lacked
   * layout state), the editor picks a sensible default split:
   * `ceil(N/2)` rows + `floor(N/2)` cols, taken from `characteristics`
   * in original order.
   */
  _rowAxes?: PropertyCharacteristic[];
  _colAxes?: PropertyCharacteristic[];
}

export interface RateFactor {
  /**
   * Auto-derived deterministic id. Computed from `kind` + `property` +
   * a stable canonicalization of `groups[]` by `computeFactorId(factor)`
   * in `RateFactorId.ts`. The user does NOT see or author this. Both
   * client and server recompute on save; manually-authored mismatches
   * are rejected by the validator.
   */
  id: string;
  kind: FactorKind;
  /** Required when `kind === 'PER_NUMERIC_PROPERTY'`. */
  property?: ItemNumericProperty;
  /**
   * Zero or more groups of propertyCharacteristics. The factor's
   * matched contribution is the SUM of the matched cell amount from
   * each group.
   *
   * Authoring rules (enforced by the validator):
   *  - Two groups MUST NOT contain the same SET of characteristics.
   *  - A characteristic MUST NOT appear twice within the same group.
   *  - `cells` MUST be non-empty.
   *  - The cell space MUST be total — every plausible value
   *    combination across the group's characteristics must resolve to
   *    SOME cell (an all-`*` cell satisfies this trivially).
   */
  groups: PropertyCharacteristicGroup[];
  /** Used only when `groups.length === 0`. Otherwise ignored. */
  baseAmount?: number;
  /** Floor for THIS factor's contribution (before global minimums). */
  perItemMinimum?: number;
  /**
   * Round the numeric property UP to a multiple of this value before
   * multiplying. Use 8 for legacy page-signature pricing.
   * Only meaningful when `kind === 'PER_NUMERIC_PROPERTY'`.
   */
  signatureRoundingMultiple?: number;
}

/** Allowed values for {@link ItemNumericProperty}, used by dropdowns. */
export const NUMERIC_PROPERTIES: ItemNumericProperty[] = ['pages', 'width', 'height', 'weight', 'thickness', 'caseQuantity'];

/**
 * Known enum-property values used by the conditions editor.
 *
 * `paperType`, `packageType`, `carrierServiceLevel` and `facility` are
 * intentionally left empty here because their allowed values are
 * dynamic — sourced from `PaperTypeService` / `PackageTypeService` /
 * `CarrierServiceService` / `FacilityService` at runtime.
 */
export const ENUM_PROPERTY_VALUES: Record<ItemEnumProperty, string[]> = {
  interiorType: ['GRAY', 'COLOR'],
  bindingType:  ['PERFECT', 'COIL'],
  laminateType: ['GLOSS', 'MATTE'],
  paperType:    [],   // dynamic — see PaperTypeService
  packageType:  [],   // dynamic — see PackageTypeService
  carrierServiceLevel: [],  // dynamic — see CarrierServiceService
  facility:     []    // dynamic — see FacilityService
};

/** Currency dropdown options. Free-form text remains supported via the input fallback. */
export const CURRENCY_OPTIONS = ['USD', 'CAD', 'EUR', 'GBP', 'AUD'];

/**
 * Default page-count values used by the preview matrix when a document
 * does not declare its own `_previewPageCounts`.
 */
export const DEFAULT_PREVIEW_PAGE_COUNTS = [48, 96, 200, 400, 700];

/**
 * Default book-quantity values used by the preview matrix when a
 * document does not declare its own `_previewBookQuantities`. Used
 * only when the active scope's factors operate at the order level —
 * the columns then become per-quantity "Price" columns evaluated via
 * {@link RateConfigEvaluator.evaluateOrder}. For pure book-level
 * production scopes the existing `defaultQuantityBreaks` tier columns
 * are used instead and this list is ignored.
 */
export const DEFAULT_PREVIEW_BOOK_QUANTITIES = [1, 10, 100, 1000];

/** Default quantity-break tiers used when a config omits `defaultQuantityBreaks`. */
export const DEFAULT_QUANTITY_BREAK_TIERS: { minQty: number; maxQty: number }[] = [
  { minQty: 1, maxQty: 4 },
  { minQty: 5, maxQty: 9 },
  { minQty: 10, maxQty: 19 },
  { minQty: 20, maxQty: 49 },
  { minQty: 50, maxQty: 99 },
  { minQty: 100, maxQty: 249 },
  { minQty: 250, maxQty: MAX_QTY_SENTINEL }
];

/** Build the cell-key token for one quantity-break tier. */
export function quantityBreakKey(tier: { minQty: number; maxQty: number }): string {
  if (tier.maxQty >= MAX_QTY_SENTINEL) { return `${tier.minQty}-`; }
  return `${tier.minQty}-${tier.maxQty}`;
}

/**
 * Pick the matching quantity-break tier for an order quantity. Returns
 * `undefined` when no tier covers the quantity.
 */
export function findQuantityBreakTier(qty: number, tiers: { minQty: number; maxQty: number }[] | undefined):
    { minQty: number; maxQty: number } | undefined {
  const list = tiers && tiers.length > 0 ? tiers : DEFAULT_QUANTITY_BREAK_TIERS;
  for (const t of list) {
    if (qty >= t.minQty && qty <= t.maxQty) { return t; }
  }
  return undefined;
}

// ── PropertyCharacteristic / FactorKind legality matrix ──────────────

/**
 * Characteristics that may NEVER appear in a factor of the given kind.
 * The validator rejects any group whose `characteristics` array
 * contains a forbidden entry for its parent factor's kind.
 *
 * Rationale:
 *  - `packageType` is meaningless for book-level factors
 *    (`PER_ITEM` / `PER_QUANTITY` / `PER_NUMERIC_PROPERTY`) because the
 *    book itself does not belong to a single package; packages are an
 *    order-level grouping iterated only by `PER_PACKAGE`.
 *  - `carrierServiceLevel` is an order-level constant, so grouping a
 *    PER-BOOK factor on it would multiply the same constant by the
 *    book count for no useful differentiation; the only legal kinds
 *    are the order-iterated ones (`PER_ORDER`, `PER_PACKAGE`).
 *  - `numberUp` derives from body width/height — a book-only concept;
 *    forbid on `PER_PACKAGE`.
 *  - `facility` is an order-level constant (every item in an order is
 *    produced/packed at one facility), BUT differentiating per-book
 *    or per-page costs by facility is a legitimate authoring need
 *    (e.g. one facility runs the press cheaper, so its per-page rate
 *    is lower) — so unlike `carrierServiceLevel`, `facility` is NOT
 *    forbidden on book-level kinds.
 *
 * Production-side `RateConfig` documents simply will not author any
 * `PER_PACKAGE` factors (no `packageTypes[]` are passed in for a
 * production rate evaluation), so production data is unaffected.
 */
export const FORBIDDEN_CHARACTERISTICS_BY_KIND: Record<FactorKind, PropertyCharacteristic[]> = {
  PER_ORDER: [],
  PER_ITEM:               ['packageType', 'carrierServiceLevel'],
  PER_QUANTITY:           ['packageType', 'carrierServiceLevel'],
  PER_NUMERIC_PROPERTY:   ['packageType', 'carrierServiceLevel'],
  PER_PACKAGE:            ['numberUp']
};

/** True iff a group of the given `kind` is allowed to contain `c` in its characteristics. */
export function isCharacteristicAllowedForKind(kind: FactorKind, c: PropertyCharacteristic): boolean {
  const forbidden = FORBIDDEN_CHARACTERISTICS_BY_KIND[kind];
  return !forbidden || forbidden.indexOf(c) < 0;
}

